interface Props { cs: boolean }

function CsBody() {
  return (
    <>
      <p>
        ShowX je <strong>LAN-first FOH aplikace</strong> na Macu, která sjednocuje cuelist, timecode, cue lights a AI
        showcaller do jednoho Electron okna. Stanice pro operátory běží v browseru — žádná instalace pro crew. Venue
        runtime nevyžaduje internet: show jede přes LAN, i když Wi-Fi vypadne.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Hierarchie: Show → Cuelist → Cue → Payload</h3>
      <p>
        ShowX pracuje se čtyřmi vrstvami od globálního kontextu po atomický výstup:
      </p>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-4 space-y-2">
          <div className="flex gap-3 items-start">
            <span className="text-accent-deep font-semibold w-28 shrink-0">Show</span>
            <span className="text-muted">Jeden soubor <code>.showx</code>. Obsahuje metadata, routing, historii a 1–N cuelistů (MVP = 1).</span>
          </div>
          <div className="flex gap-3 items-start pl-4">
            <span className="text-accent-deep font-semibold w-24 shrink-0">Cuelist</span>
            <span className="text-muted">Uspořádaná sekvence cues. Jeden sdílený show dokument se společným playheadem.</span>
          </div>
          <div className="flex gap-3 items-start pl-8">
            <span className="text-accent-deep font-semibold w-20 shrink-0">Cue</span>
            <span className="text-muted">Atom. Má label, departments, trigger, timing. <strong>Není typovaná</strong> — typ je na payload úrovni.</span>
          </div>
          <div className="flex gap-3 items-start pl-12">
            <span className="text-accent-deep font-semibold w-16 shrink-0">Payload</span>
            <span className="text-muted">Konkrétní instrukce (OSC / MIDI / DMX / webhook / …). Typovaná. Jedna cue jich může mít víc.</span>
          </div>
        </div>
      </div>
      <p>
        Za payloady stojí ještě dvě vrstvy: <strong>routing pravidlo</strong> (CO jde KAM) a <strong>device</strong>
        (IP/port + driver, který přeloží záměr do dialektu zařízení). Cue zná jen záměr — kde to skončí, určuje routing.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Inverze QLab: jedna cue, víc oddělení</h3>
      <p>
        V QLab je každá cue typovaná na cue úrovni (audio cue, MIDI cue, OSC cue). ShowX to invertuje:{' '}
        <strong>cue je netypovaná, payloady jsou typované</strong>. Praktický dopad: jedna cue "Otevření sálu" může
        zároveň zapálit světla, spustit zvuk a nastartovat video:
      </p>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-1">
          <div className="py-3 border-b border-rule/40">
            <span className="text-ink font-semibold">Cue 1 — Otevření sálu</span>
            <span className="ml-3 text-muted text-xs">departments: LX · SX · VIDEO</span>
          </div>
          <div className="py-2 pl-4 border-b border-rule/20 flex gap-3">
            <span className="text-accent-deep w-20 shrink-0">lx_ref</span>
            <span className="text-muted">→ ETC Eos: cue 0001 /go (přes routing: lx_ref → lx_eos)</span>
          </div>
          <div className="py-2 pl-4 border-b border-rule/20 flex gap-3">
            <span className="text-accent-deep w-20 shrink-0">osc</span>
            <span className="text-muted">→ QLab: /cue/1/start (přes routing: tag=SX → sx_qlab)</span>
          </div>
          <div className="py-2 pl-4 flex gap-3">
            <span className="text-accent-deep w-20 shrink-0">webhook</span>
            <span className="text-muted">→ video server: POST /play (přes routing: tag=VIDEO → video_disguise)</span>
          </div>
        </div>
      </div>
      <p>
        Jeden GO = tři zařízení spuštěna zároveň. Operátor LX vidí tuto cue jako LX cue (LX filter), operátor SX
        jako SX cue. SM vidí vše. Nikdo z nich neví o ostatních oddělení — každý dostane relevantní výřez.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Oddělení (departments)</h3>
      <p>Kanonická oddělení v ShowX:</p>
      <div className="my-4 grid grid-cols-2 gap-2 font-mono text-sm">
        {[
          ['SM', 'Stage Manager — vidí vše, má GO authority'],
          ['LX', 'Osvětlení — lighting console, DMX'],
          ['SX', 'Zvuk — audio playback, mixpult'],
          ['VIDEO', 'Video / projekce — media server'],
          ['PYRO', 'Pyrotechnika'],
          ['FS', 'Speciální efekty (fog, snow, CO₂…)'],
          ['AUTO', 'Automaticky spouštěné cues (timecode, webhook trigger)'],
        ].map(([tag, desc]) => (
          <div key={tag} className="flex gap-3 items-start p-3 rounded border border-rule/40">
            <code className="text-accent-deep font-bold w-14 shrink-0">{tag}</code>
            <span className="text-muted">{desc}</span>
          </div>
        ))}
      </div>
      <p>
        Každá cue má přiřazená oddělení. Operátorská stanice vidí jen cues svého oddělení —{' '}
        <strong>department filter</strong>. SM vidí cuelist celý a má exkluzivní GO authority.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">LAN-first architektura</h3>
      <p>
        FOH Mac běží Electron shell. Stanice (iPady, Macy, PC) připojují se přes LAN — otevřou adresu shellu v
        browseru, spárují se PINem, hotovo. Žádná instalace pro crew.
      </p>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-4">
          <div className="text-center space-y-1">
            <div className="text-ink font-semibold">FOH Mac (Electron shell)</div>
            <div className="text-muted text-xs">port :5300 · mDNS _showx._tcp.local</div>
            <div className="text-muted mt-4">↕ LAN (Yjs sync + GO events + assets)</div>
            <div className="mt-3 flex justify-center gap-6 flex-wrap">
              <div className="border border-rule/40 rounded px-3 py-2 text-center">
                <div className="text-ink">SM iPad</div>
                <div className="text-muted text-xs">PWA · role: sm</div>
              </div>
              <div className="border border-rule/40 rounded px-3 py-2 text-center">
                <div className="text-ink">LX Pult</div>
                <div className="text-muted text-xs">PWA · role: operator · dept: LX</div>
              </div>
              <div className="border border-rule/40 rounded px-3 py-2 text-center">
                <div className="text-ink">Countdown TV</div>
                <div className="text-muted text-xs">PWA · role: countdown</div>
              </div>
            </div>
            <div className="text-muted mt-4">↕ OSC / MIDI / DMX / webhook</div>
            <div className="mt-3 flex justify-center gap-4 flex-wrap text-xs text-muted">
              <span>ETC Eos</span>
              <span>QLab</span>
              <span>disguise</span>
              <span>MA2/3</span>
              <span>vlastní zařízení</span>
            </div>
          </div>
        </div>
      </div>
      <p>
        Cloud Sync je opt-in modul — Supabase backup pro cross-venue přístup. <strong>Nikdy není vyžadován</strong> pro
        venue runtime. Ani internet není podmínkou show.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Mini glosář</h3>
      <dl className="space-y-3 text-sm">
        {[
          ['cue', 'Atom spustitelné akce. Má label, departments, trigger, timing a payloady. Netypovaná na cue úrovni.'],
          ['cuelist', 'Uspořádaná sekvence cues. Jeden sdílený show dokument s playheadem.'],
          ['payload', 'Konkrétní instrukce pro zařízení (OSC / MIDI / DMX / webhook…). Typovaná na payload úrovni.'],
          ['department', 'Oddělení (LX / SX / VIDEO / …). Cue je viditelná operátorům svých oddělení.'],
          ['stanice', 'PWA v browseru na iPadu, Macu, PC. Páruje se s shellem přes PIN.'],
          ['SM', 'Stage Manager. Vidí celý cuelist, má GO authority, volá standby.'],
          ['GO', 'Spustit cue. SM stiskne GO button nebo klávesovou zkratku (Q + Space nebo hold).'],
          ['playhead', 'Ukazatel "next cue" v cuelistu. Kde jsme teď a co jde příště.'],
          ['REHEARSAL', 'Volný editovací mód. Cues lze měnit, GO je instantní.'],
          ['SHOW', 'Zamčený produkční mód. Payloady frozen, GO je hold-to-GO, změny přes proposals.'],
          ['FOH', 'Front of House. Technická kabina, kde stojí FOH Mac se shellem.'],
        ].map(([term, def]) => (
          <div key={String(term)} className="flex gap-4">
            <dt className="font-mono text-accent-deep w-28 shrink-0 pt-0.5">{term}</dt>
            <dd className="text-muted leading-relaxed">{def}</dd>
          </div>
        ))}
      </dl>
    </>
  )
}

function EnBody() {
  return (
    <>
      <p>
        ShowX is a <strong>LAN-first FOH application</strong> on a Mac that unifies cuelist, timecode, cue lights and AI
        showcaller into a single Electron window. Operator stations run in any browser — no install for crew. The venue
        runtime needs no internet: the show runs over LAN even if Wi-Fi drops.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Hierarchy: Show → Cuelist → Cue → Payload</h3>
      <p>ShowX works with four layers from global context to atomic output:</p>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-4 space-y-2">
          <div className="flex gap-3 items-start">
            <span className="text-accent-deep font-semibold w-28 shrink-0">Show</span>
            <span className="text-muted">One <code>.showx</code> file. Holds metadata, routing, history and 1–N cuelists (MVP = 1).</span>
          </div>
          <div className="flex gap-3 items-start pl-4">
            <span className="text-accent-deep font-semibold w-24 shrink-0">Cuelist</span>
            <span className="text-muted">Ordered sequence of cues. One shared show document with a single playhead.</span>
          </div>
          <div className="flex gap-3 items-start pl-8">
            <span className="text-accent-deep font-semibold w-20 shrink-0">Cue</span>
            <span className="text-muted">The atom. Has label, departments, trigger, timing. <strong>Untyped</strong> at cue level — type lives in the payload.</span>
          </div>
          <div className="flex gap-3 items-start pl-12">
            <span className="text-accent-deep font-semibold w-16 shrink-0">Payload</span>
            <span className="text-muted">Concrete instruction (OSC / MIDI / DMX / webhook / …). Typed. One cue can have several.</span>
          </div>
        </div>
      </div>
      <p>
        Behind payloads there are two more layers: a <strong>routing rule</strong> (WHAT goes WHERE) and a{' '}
        <strong>device</strong> (IP/port + driver that translates intent to the device's dialect). The cue knows only
        intent — routing decides where it ends up.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">The QLab inversion: one cue, many departments</h3>
      <p>
        In QLab every cue is typed at the cue level (audio cue, MIDI cue, OSC cue). ShowX inverts that:{' '}
        <strong>the cue is untyped, payloads are typed</strong>. Practically: one cue "Opening the hall" can
        simultaneously fire lights, sound, and video.
      </p>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-1">
          <div className="py-3 border-b border-rule/40">
            <span className="text-ink font-semibold">Cue 1 — Opening the hall</span>
            <span className="ml-3 text-muted text-xs">departments: LX · SX · VIDEO</span>
          </div>
          <div className="py-2 pl-4 border-b border-rule/20 flex gap-3">
            <span className="text-accent-deep w-20 shrink-0">lx_ref</span>
            <span className="text-muted">→ ETC Eos: cue 0001 /go (via routing: lx_ref → lx_eos)</span>
          </div>
          <div className="py-2 pl-4 border-b border-rule/20 flex gap-3">
            <span className="text-accent-deep w-20 shrink-0">osc</span>
            <span className="text-muted">→ QLab: /cue/1/start (via routing: tag=SX → sx_qlab)</span>
          </div>
          <div className="py-2 pl-4 flex gap-3">
            <span className="text-accent-deep w-20 shrink-0">webhook</span>
            <span className="text-muted">→ video server: POST /play (via routing: tag=VIDEO → video_disguise)</span>
          </div>
        </div>
      </div>
      <p>
        One GO = three devices fired simultaneously. The LX operator sees this cue as an LX cue, the SX operator sees it
        as an SX cue. The SM sees everything. Each operator only sees their relevant slice.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Departments</h3>
      <p>Canonical departments in ShowX:</p>
      <div className="my-4 grid grid-cols-2 gap-2 font-mono text-sm">
        {[
          ['SM', 'Stage Manager — sees everything, holds GO authority'],
          ['LX', 'Lighting — lighting console, DMX'],
          ['SX', 'Sound — audio playback, mixing desk'],
          ['VIDEO', 'Video / projection — media server'],
          ['PYRO', 'Pyrotechnics'],
          ['FS', 'Special effects (fog, snow, CO₂…)'],
          ['AUTO', 'Auto-fired cues (timecode, webhook trigger)'],
        ].map(([tag, desc]) => (
          <div key={tag} className="flex gap-3 items-start p-3 rounded border border-rule/40">
            <code className="text-accent-deep font-bold w-14 shrink-0">{tag}</code>
            <span className="text-muted">{desc}</span>
          </div>
        ))}
      </div>
      <p>
        Every cue has assigned departments. An operator station only shows cues for its department —{' '}
        <strong>department filter</strong>. The SM sees the whole cuelist and has exclusive GO authority.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">LAN-first architecture</h3>
      <p>
        The FOH Mac runs the Electron shell. Stations (iPads, Macs, PCs) connect over LAN — they open the shell's
        address in a browser, pair with a PIN, done. No install for crew.
      </p>
      <p>
        Cloud Sync is an opt-in module — Supabase backup for cross-venue access.{' '}
        <strong>It is never required</strong> for the venue runtime. The show needs no internet.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Mini glossary</h3>
      <dl className="space-y-3 text-sm">
        {[
          ['cue', 'Atom of fireable action. Has label, departments, trigger, timing and payloads. Untyped at cue level.'],
          ['cuelist', 'Ordered sequence of cues. One shared show document with a playhead.'],
          ['payload', 'Concrete instruction for a device (OSC / MIDI / DMX / webhook…). Typed at payload level.'],
          ['department', 'Department (LX / SX / VIDEO / …). Cue is visible to operators of its departments.'],
          ['station', 'PWA in a browser on iPad, Mac, PC. Pairs with the shell via PIN.'],
          ['SM', 'Stage Manager. Sees the whole cuelist, holds GO authority, calls standby.'],
          ['GO', 'Fire a cue. SM presses GO button or keyboard shortcut (Q + Space or hold).'],
          ['playhead', '"Next cue" pointer in the cuelist. Where we are and what is next.'],
          ['REHEARSAL', 'Free edit mode. Cues are editable, GO is instant.'],
          ['SHOW', 'Locked production mode. Payloads frozen, GO is hold-to-GO, edits via proposals.'],
          ['FOH', 'Front of House. The technical booth where the FOH Mac with the shell lives.'],
        ].map(([term, def]) => (
          <div key={String(term)} className="flex gap-4">
            <dt className="font-mono text-accent-deep w-28 shrink-0 pt-0.5">{term}</dt>
            <dd className="text-muted leading-relaxed">{def}</dd>
          </div>
        ))}
      </dl>
    </>
  )
}

export default function Chapter1({ cs }: Props) {
  return (
    <article id="kap-1" className="scroll-mt-24">
      <h2 className="display-serif text-display-2 leading-tight mb-8">
        {cs ? '1. Jak ShowX přemýšlí' : '1. How ShowX thinks'}
      </h2>
      <div className="copy prose-guide max-w-none">
        {cs ? <CsBody /> : <EnBody />}
      </div>
    </article>
  )
}
